import React, { useCallback, useEffect, useState } from 'react';
import { View, Modal, Text } from 'react-native';
import PropTypes from 'prop-types';
import {
    NavigationPicker,
    NavigationPickerHeader,
    Icon,
    ActivityIndicator,
    Background,
    ConfirmationModal,
    useConfirmationModal
} from 'LocalGluons';
import { t } from 'Locale';
import { useStore } from 'Store';
import { Device, getUniqueId, unlockAllOrientations } from 'DeviceUtils';
import { useRoute, CommonActions } from '@react-navigation/native';
import { getAddedFavorites, getDeletedFavorites } from 'Utils/channels/favorites';
import { logEvent } from 'AppManager';
import PopUp from 'Pages/FavoriteSettings/components/PopUp';
import { useFavoriteChannelsContext } from 'LocalGluons/components/FavoriteChannelsContext/FavoriteChannelsContext';

import { useSettingsNavigation } from 'Pages/Settings/components/SettingsNavigationContext/SettingsNavigationContext';
import FavoriteSettingsTabs from './components/FavoriteSettingsTabs';
import { ActionList } from './components/ActionList';

import { useFavoriteSettingsContext } from './FavoriteSettingsContext';
import baseStyles from './FavoritesSettings.jss';
import { IconBack } from '../../Navigation/navigationHeaderIcons';
import { EPG_LIST_TYPES } from '../../../options';
import { useNavigationPropsContext } from '../../Navigation/navigationPropsContext';
import { TOP_BAR_HEIGHT, TOP_BAR_TOTAL_HEIGHT } from '../../Navigation/Menu/Menu.styles';

const COPY_FAVORITE = 'SETTINGS.FAVOURITE_CHANNELS_COPY_TO_DEVICE';
const RESET_FAVORITE = 'SETTINGS.FAVOURITE_CHANNELS_RESET';

/* eslint max-statements: [0] */
const FavoritesSettings = ({ navigation }) => {
    const route = useRoute();
    const { appManager } = useStore();
    const {
        orderedFavoriteList,
        setOrderedFavoriteList,
        activeDevice,
        setActiveDevice,
        setChannelMap,
        channelMap,
        activeTab,
        setActiveTab,
        setDataChanged,
        navigationPickerHeaderLayout,
        navigationPickerWidth
    } = useFavoriteSettingsContext();
    const { setFavoriteChannels } = useFavoriteChannelsContext();
    const { setModalContent } = useConfirmationModal();
    const {
        previousRoute,
        setPreviousRoute,
        returnToPrevious: shouldReturnToPrevious,
        setReturnToPrevious
    } = useSettingsNavigation();
    const [loaded, setLoaded] = useState(false);

    const [refresh, setRefresh] = useState(true);
    const [devices, setDevices] = useState([]);

    const [popupContent, setPopupContent] = useState(null);
    const [changeDeviceTo, setChangeDeviceTo] = useState(null);

    const [togg, setTogg] = useState(false);
    // Stores last saved data
    const snapshotData = React.useRef([]);
    const isDataSnapshotData = snapshotData.current.length === 0;
    const { updateNavigationProps, navigate, navigationProps } = useNavigationPropsContext();

    useEffect(() => {
        if (!snapshotData.current) setRefresh(true);
        return () => {
            updateNavigationProps({ FavoriteDevicePickerValue: null });
            setActiveDevice(null);
            if (Device.isPhone) {
                // Enable orientation rotation when returning back to EPG
                const prevRoutes = navigation.getParent().getState().routes;
                if (prevRoutes[prevRoutes.length - 1]?.routeName === 'TvGuide') {
                    unlockAllOrientations();
                }
            }
        };
    }, []);

    const saveFavoriteOrderCallback = useCallback((params, onSuccess, onError) => {
        const saveOrderedFavoriteList = async () => {
            try {
                await appManager.api.setCustomChannelMap(params);
                await setFavoriteChannels(false);
                setRefresh(true);
                if (onSuccess && typeof onSuccess === 'function') {
                    onSuccess();
                }
            } catch (error) {
                setLoaded(true);
                if (onError && typeof onError === 'function') {
                    onError();
                }
            }
        };
        setLoaded(false);

        saveOrderedFavoriteList();
    }, []);
    // Callback sets also a head title

    const onDevicePickerCallback = useCallback(
        (device) => {
            let changeFavoriteMap;
            const isChangeInFavorites =
                snapshotData.current?.length !== orderedFavoriteList?.length;

            if (isChangeInFavorites) {
                changeFavoriteMap = true;
            } else {
                for (const index in orderedFavoriteList) {
                    if (orderedFavoriteList[index] !== snapshotData.current[index]) {
                        changeFavoriteMap = true;
                        break;
                    }
                }
            }
            // If active device is re-selected just close the picker
            if (activeDevice && activeDevice.id === device.id && loaded) {
                updateNavigationProps({
                    FavoriteDevicePickerIsToggled: false
                });
            } else if (snapshotData.current && changeFavoriteMap && !isDataSnapshotData) {
                // Confirmation dialog save/cancel current changes
                setChangeDeviceTo(device);
            } else {
                setRefresh(true);
                setChangeDeviceTo(null);
                setActiveDevice(device);
                setOrderedFavoriteList(device.order, device);

                snapshotData.current = device.order;
                updateNavigationProps({
                    FavoriteDevicePickerIsToggled: false,
                    FavoriteDevicePickerValue: device.title,
                    FavoriteDevicePickerValuePostfix: device.postfix
                });
            }
        },
        [orderedFavoriteList, loaded, channelMap]
    );

    const fetchDeviceConfig = async (forceRefresh = false) => {
        try {
            const [tmpDevices, tmpChannels, listDevices] = await Promise.all([
                appManager.api.fetchCustomChannelMapList(),
                appManager.api.fetchChannelsBrief({
                    list: activeDevice?.isDeviceIptv ? 'all' : 'live'
                }),
                appManager.api.listDevices()
            ]);
            const newChannelMap = tmpChannels.items.reduce((result, channel) => {
                const { channelId, logoUrl, name } = channel;
                result[channelId] = {
                    logoUrl,
                    name
                };
                return result;
            }, {});

            // Get Id of current device
            const currentDeviceId = await getUniqueId();
            const newDevices = tmpDevices.data.reduce((result, device) => {
                const item = {
                    id: device.id,
                    isDeviceIptv: !!device.iptvDevice,
                    deviceGoId: device.deviceGoId || undefined,
                    value: device.deviceGoId || device.id,
                    title: device.name,
                    order: device.order,
                    category: device.ottDevice?.category
                };

                const isCurrentDevice =
                    device?.ottDevice && device?.ottDevice?.dsid === currentDeviceId;
                const isWebCurrentDevice =
                    listDevices?.thisDevice?.category === item?.category &&
                    item?.category === 'WEB_BROWSER';

                if (isCurrentDevice || isWebCurrentDevice) {
                    item.postfix = ` (${t('COMMON.THIS_DEVICE')})`;
                    // Set it as the first one
                    result.unshift(item);
                } else {
                    result.push(item);
                }
                return result;
            }, []);

            setChannelMap(newChannelMap);
            setDevices(newDevices);

            if (!activeDevice || isDataSnapshotData || forceRefresh) {
                const newActiveDevice = activeDevice
                    ? newDevices.find((device) => device.deviceGoId === activeDevice.deviceGoId)
                    : newDevices[0];

                // Set active device via callback
                onDevicePickerCallback(newActiveDevice || newDevices[0]);
                snapshotData.current = newActiveDevice.order;
                if (forceRefresh) setOrderedFavoriteList(newActiveDevice.order, newActiveDevice);
            }
            setRefresh(false);
            setLoaded(true);
            setDataChanged(false);
        } catch (error) {
            console.log(error);
        }
    };
    const onActionPickerCallback = useCallback(
        ({ value }) => {
            if (value === COPY_FAVORITE) {
                navigate('CopyFavorites', {
                    activeDevice,
                    devices: devices.filter(({ id }) => id !== activeDevice.id),
                    deviceOnPressCallback: (device) =>
                        saveFavoriteOrderCallback(
                            {
                                order: orderedFavoriteList,
                                deviceGoId: device.deviceGoId || undefined,
                                toDefault: false,
                                id: device.isDeviceIptv ? device.id : undefined,
                                category: device?.ottDevice?.category
                            },
                            () =>
                                setPopupContent(
                                    `${t('SETTINGS.FAVOURITE_CHANNELS_COPY_SUCCESS')} ${
                                        device.title
                                    }`
                                ),
                            () => setPopupContent(t('SETTINGS.FAVOURITE_CHANNELS_COPY_FAILED'))
                        )
                });
            } else if (value === RESET_FAVORITE) {
                saveFavoriteOrderCallback(
                    {
                        order: orderedFavoriteList,
                        deviceGoId: activeDevice.deviceGoId || undefined,
                        id: activeDevice.isDeviceIptv ? activeDevice.id : undefined,
                        toDefault: true
                    },
                    () => {
                        setLoaded(false);
                        fetchDeviceConfig(true);
                        setDataChanged(true);
                    }
                );
            }

            updateNavigationProps({ FavoriteActionsPickerIsToggled: false });
        },
        [orderedFavoriteList, activeDevice]
    );

    const onClosePopupCallback = useCallback(() => {
        setPopupContent(null);
    }, []);

    const returnToPrevious = useCallback(() => {
        if (Device.isTabletOrWeb && previousRoute) {
            if (Device.isTablet) navigation.dismiss();
            navigate(previousRoute);
            setPreviousRoute(null);
        }
        if (Device.isPhone) navigation.goBack();

        setPreviousRoute(null);
        setReturnToPrevious(false);
    }, [navigation, previousRoute]);

    const onCancelCallback = useCallback(() => {
        navigation.pop();
        setOrderedFavoriteList(snapshotData.current, activeDevice);
    }, [previousRoute, navigation, shouldReturnToPrevious]);

    const onSaveCallback = useCallback(
        async (newOrderedFavoriteList) => {
            setDataChanged(false);
            if (snapshotData.current !== newOrderedFavoriteList) {
                let localOrderedFavoriteList = newOrderedFavoriteList;
                if (!newOrderedFavoriteList.length) {
                    await appManager.api.setCustomChannelMap({
                        order: newOrderedFavoriteList,
                        deviceGoId: activeDevice.deviceGoId || undefined,
                        id: activeDevice.isDeviceIptv ? activeDevice.id : undefined,
                        toDefault: false
                    });
                    const { items } = await appManager
                        .getApi()
                        .fetchChannelsBrief({ list: EPG_LIST_TYPES.FAVORITES }, true, false);
                    if (items) {
                        localOrderedFavoriteList = items?.map(({ channelId }) => channelId);
                        setOrderedFavoriteList(localOrderedFavoriteList, activeDevice);
                    }
                }

                const added = getAddedFavorites(snapshotData.current, localOrderedFavoriteList);
                if (added && added.length > 0) {
                    logEvent({
                        type: 'action',
                        subtype: 'favoritesAdd',
                        content1: added.toString()
                    });
                }
                const deleted = getDeletedFavorites(snapshotData.current, localOrderedFavoriteList);
                if (deleted && deleted.length > 0) {
                    logEvent({
                        type: 'action',
                        subtype: 'favoritesDelete',
                        content1: deleted.toString()
                    });
                }
                saveFavoriteOrderCallback({
                    order: localOrderedFavoriteList,
                    deviceGoId: activeDevice.deviceGoId || undefined,
                    id: activeDevice.isDeviceIptv ? activeDevice.id : undefined,
                    toDefault: false
                });
                snapshotData.current = localOrderedFavoriteList;

                if (shouldReturnToPrevious) {
                    returnToPrevious();
                } else {
                    // TO DO: find out how find the screen
                    const navigateAction = CommonActions.navigate({
                        name: 'FavoriteSettingsTabs',
                        params: {}
                    });
                    navigation.dispatch(navigateAction);
                }
            }
        },
        [activeDevice, shouldReturnToPrevious, navigation, fetchDeviceConfig, snapshotData]
    );

    const onDialogCloseCallback = useCallback(() => {
        setChangeDeviceTo();
    }, []);

    const onDialogConfirmCallback = useCallback(() => {
        onSaveCallback(orderedFavoriteList);
        onDevicePickerCallback(changeDeviceTo);
    }, [onSaveCallback, orderedFavoriteList, changeDeviceTo, onDevicePickerCallback]);

    const onDialogCancelCallback = useCallback(() => {
        // Prevent from re-opening this dialog
        snapshotData.current = null;
        onDevicePickerCallback(changeDeviceTo);
    }, [onDevicePickerCallback, changeDeviceTo]);

    useEffect(() => {
        if (refresh) {
            fetchDeviceConfig();
        }
    }, [refresh]);

    const devicePickerIsDisplayed = navigationProps?.FavoriteDevicePickerIsToggled || undefined;
    const actionsPickerIsToggled = navigationProps?.FavoriteActionsPickerIsToggled || undefined;

    const getDevicePickerStyling = () => {
        if (Device.isWeb) {
            if (navigationPickerWidth === 0) return [baseStyles.devicePicker, { opacity: 0 }];
            if (navigationPickerWidth)
                if (navigationPickerWidth > navigationPickerHeaderLayout.width) {
                    const calculation =
                        (navigationPickerWidth - navigationPickerHeaderLayout.width) / 2;
                    const devicePickerStartPoint = navigationPickerHeaderLayout.x - calculation;
                    return [baseStyles.devicePicker, { left: devicePickerStartPoint, opacity: 1 }];
                }
            const calculation = (navigationPickerHeaderLayout.width - navigationPickerWidth) / 2;
            const devicePickerStartPoint = navigationPickerHeaderLayout.x + calculation;
            return [baseStyles.devicePicker, { left: devicePickerStartPoint, opacity: 1 }];
        }
        return baseStyles.devicePicker;
    };
    const confirmСhanges = (newOrderedFavoriteList) => {
        setModalContent(
            <ConfirmationModal
                header={t('SETTINGS.FAV_CHNLS_SAVE_CHANGES_H')}
                message={t('SETTINGS.FAV_CHNLS_SAVE_CHANGES_TXT').replace(
                    '""',
                    `"${activeDevice.title}"`
                )}
                confirmLabel={t('COMMON.BTN_SAVE')}
                cancelLabel={t('COMMON.BTN_DONT_SAVE')}
                onCancel={() => setModalContent(null)}
                onConfirm={() => {
                    setModalContent(null);
                    onSaveCallback(newOrderedFavoriteList);
                }}
                onClose={() => setModalContent(null)}
                style={baseStyles.confirmationModalContainer}
            />
        );
    };
    useEffect(() => {
        updateNavigationProps({ FavoriteDevicePickerIsToggled: togg });
    }, [togg]);

    const devicePickerIsToggled = navigationProps?.FavoriteDevicePickerIsToggled || false;
    const favoriteDevicePickerValue = navigationProps?.FavoriteDevicePickerValue || '';
    const favoriteDevicePickerValuePostfix =
        navigationProps?.FavoriteDevicePickerValuePostfix || '';
    return (
        <Background>
            {!Device.isPhone && favoriteDevicePickerValue !== '' && (
                <View
                    style={{
                        height: 50,
                        width: '100%',
                        flexDirection: 'row',
                        // paddingTop: Device.isWeb ? TOP_BAR_TOTAL_HEIGHT : null,
                        transition: Device.isWeb ? 'margin .16s linear' : null
                    }}
                >
                    <View style={[baseStyles.headerTitle, { flex: 4 }]}>
                        <NavigationPickerHeader
                            isToggled={devicePickerIsToggled}
                            selectedItemLabel={
                                <>
                                    {favoriteDevicePickerValue}
                                    {favoriteDevicePickerValuePostfix && (
                                        <Text style={baseStyles.currentDeviceLabel}>
                                            {favoriteDevicePickerValuePostfix}
                                        </Text>
                                    )}
                                </>
                            }
                            onToggle={() => {
                                //     console.log(
                                //         'Toggling FavoriteDevicePickerIsToggled:',
                                //         !devicePickerIsToggled
                                //     );
                                //     updateNavigationProps({
                                //         FavoriteDevicePickerIsToggled: !devicePickerIsToggled
                                //     });
                                // }}
                                setTogg(!togg);
                                updateNavigationProps({
                                    FavoriteDevicePickerIsToggled: !false
                                });
                            }}
                        />
                    </View>
                    <Icon
                        style={{ width: 29, height: 29, marginTop: 12, marginEnd: 22 }}
                        onPress={() => {
                            updateNavigationProps({
                                FavoriteActionsPickerIsToggled: !actionsPickerIsToggled
                            });
                        }}
                        name="nav_settings"
                    />
                </View>
            )}
            {/* //MARK: OD Tadial je mobil */}
            <View style={baseStyles.container}>
                <PopUp
                    visible={Boolean(popupContent)}
                    title={popupContent}
                    onConfirm={onClosePopupCallback}
                />
                {Boolean(changeDeviceTo) && (
                    <Modal
                        animationType="slide"
                        transparent
                        visible={Boolean(changeDeviceTo)}
                        onRequestClose={onDialogCloseCallback}
                    >
                        <ConfirmationModal
                            header={t('SETTINGS.FAV_CHNLS_SAVE_CHANGES_H')}
                            message={t('SETTINGS.FAV_CHNLS_SAVE_CHANGES_TXT').replace(
                                '""',
                                `"${activeDevice.title}"`
                            )}
                            confirmLabel={t('COMMON.BTN_SAVE')}
                            cancelLabel={t('COMMON.BTN_DONT_SAVE')}
                            onCancel={onDialogCancelCallback}
                            onConfirm={onDialogConfirmCallback}
                            onClose={onDialogCloseCallback}
                        />
                    </Modal>
                )}
                {Device.isWeb
                    ? togg && (
                          <NavigationPicker
                              isDisplayed={devicePickerIsDisplayed}
                              selectedItem={activeDevice}
                              items={devices}
                              onSelect={onDevicePickerCallback}
                              isDisplayedInFavoritesSettings={Device.isWeb}
                          />
                      )
                    : devicePickerIsDisplayed && (
                          <View style={getDevicePickerStyling()}>
                              <NavigationPicker
                                  isDisplayed={devicePickerIsDisplayed}
                                  selectedItem={activeDevice}
                                  items={devices}
                                  onSelect={onDevicePickerCallback}
                                  isDisplayedInFavoritesSettings={Device.isWeb}
                              />
                          </View>
                      )}
                {actionsPickerIsToggled && ( // this is MARK:settings togle window
                    <View style={baseStyles.actionPicker}>
                        <ActionList
                            selectedItem={{}}
                            data={[
                                {
                                    title: t(COPY_FAVORITE),
                                    value: COPY_FAVORITE,
                                    iconName: 'copy_solid',
                                    testID: 'copy_to',
                                    key: 'copy_to'
                                },
                                {
                                    title: t(RESET_FAVORITE),
                                    value: RESET_FAVORITE,
                                    iconName: 'reset_solid',
                                    testID: 'reset_favorites',
                                    key: 'reset_favorites'
                                }
                            ]}
                            onItemPress={onActionPickerCallback}
                        />
                    </View>
                )}
                {Device.isTablet && <View style={baseStyles.favoriteSettingsTabsContainer} />}
                <FavoriteSettingsTabs
                    screenProps={{
                        onSaveCallback: (newOrderedFavoriteList) =>
                            confirmСhanges(newOrderedFavoriteList),
                        onCancelCallback
                    }}
                    onNavigationStateChange={(_, __, action) => {
                        setActiveTab(action.routeName);
                    }}
                    isDeviceIPTV={activeDevice?.isDeviceIptv}
                />
                {!loaded && <ActivityIndicator isOverlay />}
            </View>
        </Background>
    );
};

const favoritesSettingsNavigationOptions = ({ navigation, route, headerTitleStyle }) => {
    const { navigationProps, updateNavigationProps } = useNavigationPropsContext();
    const devicePickerIsToggled = navigationProps?.FavoriteDevicePickerIsToggled || false;
    const actionsPickerIsToggled = navigationProps?.FavoriteActionsPickerIsToggled || false;
    const favoriteDevicePickerValue = navigationProps?.FavoriteDevicePickerValue || '';
    const favoriteDevicePickerValuePostfix =
        navigationProps?.FavoriteDevicePickerValuePostfix || '';

    // Devices were not loaded so far
    if (!favoriteDevicePickerValue) {
        return {
            ...(Device.isPhone && { headerLeft: IconBack }),
            headerTitle: '',
            headerRight: null,
            animation: !Device.isWeb ? 'slide_from_right' : 'none'
        };
    }

    return {
        // For phones, back button must be displayed in header,
        // otherwise there is a menu toggle button
        ...(Device.isPhone && {
            headerLeft: IconBack,
            headerRight: () => (
                <Icon
                    style={{
                        width: 29,
                        height: 29,
                        marginTop: 29,
                        marginBottom: 20,
                        marginEnd: 13
                    }}
                    onPress={() => {
                        updateNavigationProps({
                            FavoriteActionsPickerIsToggled: !actionsPickerIsToggled
                        });
                    }}
                    name="nav_settings"
                />
            ),
            headerTitle: () => (
                // This is header device dropdown MARK:toggle picker
                <View style={[baseStyles.headerTitle, headerTitleStyle]}>
                    <NavigationPickerHeader
                        isToggled={devicePickerIsToggled}
                        selectedItemLabel={
                            <>
                                {favoriteDevicePickerValue}
                                {favoriteDevicePickerValuePostfix && (
                                    <Text style={baseStyles.currentDeviceLabel}>
                                        {favoriteDevicePickerValuePostfix}
                                    </Text>
                                )}
                            </>
                        }
                        onToggle={() => {
                            updateNavigationProps({
                                FavoriteDevicePickerIsToggled: !devicePickerIsToggled
                            });
                        }}
                    />
                </View>
            ),
            animation: !Device.isWeb ? 'slide_from_right' : 'none'
        })
    };
};

// doesn't work since V5; need to pass with options in Navigator.screen
FavoritesSettings.navigationOptions = favoritesSettingsNavigationOptions;

FavoritesSettings.defaultProps = {
    headerTitleStyle: {}
};

FavoritesSettings.propTypes = {
    navigation: PropTypes.shape({
        navigate: PropTypes.func.isRequired
    }).isRequired,
    headerTitleStyle: PropTypes.shape({})
};

export { FavoritesSettings, favoritesSettingsNavigationOptions };
